import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { CheckCircle, ShieldCheck, WarningCircle } from "@phosphor-icons/react";
import { API } from "../lib/api";

export default function BulletinValidationPage() {
  const { code } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    fetch(`${API}/learning-bulletins/validate/${encodeURIComponent(code || "")}`, {
      credentials: "omit",
      headers: { Accept: "application/json" },
    }).then(async (response) => {
      if (!response.ok) throw new Error(response.status === 404 ? "Documento não encontrado" : "Não foi possível validar o documento");
      return response.json();
    }).then((payload) => { if (!cancelled) setData(payload); })
      .catch((requestError) => { if (!cancelled) setError(requestError.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [code]);

  const valid = Boolean(data?.valid);
  return (
    <main className="min-h-screen bg-background">
      <header className="border-b border-border bg-card">
        <div className="max-w-4xl mx-auto px-6 py-5 flex items-center justify-between gap-4">
          <Link to="/login" className="font-display font-black text-2xl tracking-tighter text-foreground">SAKI<span className="text-primary">/</span>X<span className="text-[0.7em]">m</span></Link>
          <span className="label-eyebrow">validação documental</span>
        </div>
      </header>
      <div className="max-w-4xl mx-auto px-6 py-14">
        {loading ? <div className="border border-border bg-card p-12 text-center label-eyebrow">validando documento…</div>
          : error ? <div className="border border-destructive bg-destructive/5 p-8 text-center"><WarningCircle size={42} weight="fill" className="text-destructive mx-auto mb-4" /><h1 className="font-display text-3xl font-black">Documento não validado.</h1><p className="text-muted-foreground mt-2">{error}</p></div>
            : <>
              <section className={`border-2 p-7 ${valid ? "border-[hsl(var(--success))] bg-[hsl(var(--success))]/5" : "border-[hsl(var(--warning))] bg-[hsl(var(--warning))]/5"}`}>
                <div className="flex flex-col sm:flex-row sm:items-center gap-5">
                  {valid ? <ShieldCheck size={54} weight="fill" className="text-[hsl(var(--success))] shrink-0" /> : <WarningCircle size={54} weight="fill" className="text-[hsl(var(--warning))] shrink-0" />}
                  <div><div className="label-eyebrow">consulta concluída</div><h1 className="font-display text-4xl font-black tracking-tight mt-1">{valid ? "Documento autêntico e vigente." : "Emissão substituída."}</h1><p className="text-sm text-muted-foreground mt-2">{valid ? "Os dados abaixo conferem com a emissão registrada pelo SAKI/Xm." : "Este código pertence a uma emissão real, mas uma correção ou publicação posterior tornou esta versão não vigente."}</p></div>
                </div>
              </section>

              <section className="border border-border bg-card mt-6">
                <div className="px-6 py-4 border-b border-border"><div className="label-eyebrow">prova documental</div><h2 className="font-display text-2xl font-black mt-1">Dados conferidos</h2></div>
                <div className="grid sm:grid-cols-2">
                  {[
                    ["Documento", data.document_id],
                    ["Revisão", data.document_revision],
                    ["Aluno", data.student?.name],
                    ["CPF", data.student?.cpf],
                    ["Prova", data.exam?.name],
                    ["Semestre", data.exam?.semester],
                    ["Realização", data.exam?.date || "não informada"],
                    ["Resultado", `${Number(data.result?.score || 0).toFixed(1)}% · ${data.result?.hits || 0}/${data.result?.total_valid || 0} acertos`],
                    ["Posição", `${data.result?.rank || 0}º entre ${data.result?.participants || 0}`],
                    ["Emitido em", data.issued_at ? new Date(data.issued_at).toLocaleString("pt-BR") : "não informado"],
                  ].map(([label, value]) => <div key={label} className="border-b border-border sm:odd:border-r p-5"><div className="label-eyebrow">{label}</div><div className="font-mono-data font-bold mt-2 break-words">{value || "—"}</div></div>)}
                </div>
                <div className="p-5 bg-muted/20"><div className="label-eyebrow">SHA-256 do conteúdo</div><div className="font-mono-data text-xs mt-2 break-all">{data.document_sha256}</div></div>
              </section>
              <div className="mt-6 flex items-start gap-3 text-xs text-muted-foreground"><CheckCircle size={17} className="text-primary shrink-0" /><p>A consulta pública mascara o nome e o CPF. O boletim PDF completo permanece disponível somente ao aluno e à equipe autorizada.</p></div>
            </>}
      </div>
    </main>
  );
}
